import { useState } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';

// Pyramid rows from top (narrowest) to bottom (widest), sum = 75
const PYRAMID_ROWS = [3, 5, 7, 7, 9, 9, 11, 11, 13];

// Tier per row (top to bottom): top 2 elite, next 3 premium, last 4 standard
const ROW_TIERS = ['title', 'title', 'premium', 'premium', 'premium', 'standard', 'standard', 'standard', 'standard'];

const TIER_COLORS = {
  standard: { bg: 'bg-blue-500', border: 'border-blue-400', label: 'Standard ($5+)', hex: '#3B82F6' },
  premium: { bg: 'bg-purple-500', border: 'border-purple-400', label: 'Premium ($25+)', hex: '#8B5CF6' },
  title: { bg: 'bg-amber-500', border: 'border-amber-400', label: 'Elite ($100+)', hex: '#F59E0B' },
};

export default function CastleVisualization({ bricks = [], goal = 75 }) {
  const [hoveredBrick, setHoveredBrick] = useState(null);

  // Group bricks by amount-based tier, then place each tier in its dedicated row group.
  // Within each tier, fill the bottommost row first, then upward.
  const queues = {
    title: bricks.filter((b) => b.tier === 'title'),
    premium: bricks.filter((b) => b.tier === 'premium'),
    standard: bricks.filter((b) => b.tier === 'standard'),
  };

  const rowsBottomToTop = [];
  for (let rowIdx = PYRAMID_ROWS.length - 1; rowIdx >= 0; rowIdx--) {
    const tier = ROW_TIERS[rowIdx];
    const queue = queues[tier];
    const count = PYRAMID_ROWS[rowIdx];
    const row = [];
    for (let i = 0; i < count; i++) {
      if (queue.length > 0) {
        row.push({ ...queue.shift(), filled: true });
      } else {
        row.push({ filled: false });
      }
    }
    rowsBottomToTop.push(row);
  }

  const displayRows = [...rowsBottomToTop].reverse();

  const bricksUsed = bricks.length;
  const progress = Math.min((bricksUsed / goal) * 100, 100);

  return (
    <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-white">Week {Math.ceil((new Date().getDate()) / 7)} Castle</h3>
          <p className="text-xs text-gray-500 mt-1">Click on any brick to view details</p>
        </div>
        <div className="flex items-center gap-4">
          {Object.entries(TIER_COLORS).map(([tier, config]) => (
            <div key={tier} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${config.bg}`} />
              <span className="text-xs text-gray-400">{config.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pyramid */}
      <div className="flex flex-col items-center gap-1 py-8 bg-[#14161f] rounded-lg relative">
        {displayRows.map((row, rowIdx) => (
          <div key={rowIdx} className="flex gap-1 justify-center">
            {row.map((brick, colIdx) => {
              const uniqueKey = `${rowIdx}-${colIdx}`;
              const tier = brick.filled ? TIER_COLORS[brick.tier] || TIER_COLORS.standard : null;

              return (
                <div
                  key={uniqueKey}
                  className="relative"
                  onMouseEnter={() => brick.filled && setHoveredBrick(uniqueKey)}
                  onMouseLeave={() => setHoveredBrick(null)}
                >
                  <div
                    className={`w-10 h-6 rounded-sm transition-all cursor-default ${
                      brick.filled
                        ? `${tier.bg} border ${tier.border} hover:brightness-125 cursor-pointer`
                        : 'bg-gray-800/50 border border-dashed border-gray-700'
                    }`}
                  />
                  {/* Tooltip */}
                  {brick.filled && hoveredBrick === uniqueKey && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                      <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl whitespace-nowrap">
                        <p className="text-sm font-medium text-white">{brick.donor_name}</p>
                        <p className="text-xs text-cyan-400 mt-0.5">{formatCurrency(brick.amount)}</p>
                        {brick.message && (
                          <p className="text-xs text-gray-400 mt-1 max-w-[200px] truncate">{brick.message}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">Castle Progress</span>
          <span className="text-sm text-gray-400">{bricksUsed} / {goal} bricks</span>
        </div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
